'use client';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { CustomerDetail } from '@/lib/api/customers';
import { Download, FlaskConical } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { BackButton } from '../../../../components/ui/BackButton';
import { CopyableResourceBadge } from '../../../../components/ui/copyable-resource-badge';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { CustomerTagsBadge } from '@/components/customer/CustomerTagsBadge';
import { trpc } from '@/lib/api/trpc';
import { DateDisplay } from '@/components/date-display';
import { useFormatDate } from '@/hooks/use-format-date';

interface CustomerHeaderProps {
  customer: CustomerDetail;
}

export function CustomerHeader({ customer }: CustomerHeaderProps) {
  const { toast } = useToast();
  const { t } = useTranslation('customers');
  const { orgRoutes } = useOrgNavigate();
  const utils = trpc.useUtils();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const { format: formatTz, isReady: isTzReady } = useFormatDate();
  
  // Check if this is a draft/test customer
  const isDraftCustomer = (customer as any).draft === true;

  // Tag mutation hooks
  const addTagMutation = trpc.customers.addTag.useMutation({
    onSuccess: () => {
      utils.customers.getById.invalidate({ customerId: customer.id });
      toast({
        title: t('common:success'),
        description: t('tags.tagAdded'),
      });
    },
    onError: (error) => {
      toast({
        title: t('common:error'),
        description: error.message || t('tags.tagAddError'),
        variant: 'destructive',
      });
    },
  });

  const removeTagMutation = trpc.customers.removeTag.useMutation({
    onSuccess: () => {
      utils.customers.getById.invalidate({ customerId: customer.id });
      toast({
        title: t('common:success'),
        description: t('tags.tagRemoved'),
      });
    },
    onError: (error) => {
      toast({
        title: t('common:error'),
        description: error.message || t('tags.tagRemoveError'),
        variant: 'destructive',
      });
    },
  });

  const handleAddTag = async (tag: string) => {
    await addTagMutation.mutateAsync({
      customerId: customer.id,
      tag,
    });
  };

  const handleRemoveTag = async (tag: string) => {
    await removeTagMutation.mutateAsync({
      customerId: customer.id,
      tag,
    });
  };

  const handleExport = useCallback(() => {
    if (!customer) return;

    // Helper function to format address
    const formatAddress = (address: any) => {
      if (!address) return 'N/A';
      return `${address.address1 || ''} ${address.address2 || ''} ${address.city || ''} ${address.state || ''} ${address.postal || ''} ${address.country || ''}`.trim();
    };

    // Helper function to format name from address
    const formatNameFromAddress = (address: any) => {
      if (!address) return 'N/A';
      return `${address.firstName || ''} ${address.lastName || ''}`.trim() || 'N/A';
    };

    // Create CSV content with all fields
    const csvData = [
      [
        'Customer ID',
        'First Name',
        'Last Name',
        'Email',
        'Created At',
        'Billing Address - Name',
        'Billing Address - Company',
        'Billing Address - Address1',
        'Billing Address - Address2',
        'Billing Address - City',
        'Billing Address - State',
        'Billing Address - Postal',
        'Billing Address - Country',
        'Billing Address - Phone',
        'Billing Address - Email',
        'Shipping Address - Name',
        'Shipping Address - Company',
        'Shipping Address - Address1',
        'Shipping Address - Address2',
        'Shipping Address - City',
        'Shipping Address - State',
        'Shipping Address - Postal',
        'Shipping Address - Country',
        'Shipping Address - Phone',
        'Shipping Address - Email',
      ],
      [
        customer.id || 'N/A',
        customer.firstName || 'N/A',
        customer.lastName || 'N/A',
        customer.email || 'N/A',
        customer.createdAt ? formatTz(customer.createdAt, 'yyyy-MM-dd HH:mm:ss') : 'N/A',
        formatNameFromAddress(customer.billingAddress),
        customer.billingAddress?.company || 'N/A',
        customer.billingAddress?.address1 || 'N/A',
        customer.billingAddress?.address2 || 'N/A',
        customer.billingAddress?.city || 'N/A',
        customer.billingAddress?.state || 'N/A',
        customer.billingAddress?.postal || 'N/A',
        customer.billingAddress?.country || 'N/A',
        customer.billingAddress?.phone || 'N/A',
        customer.billingAddress?.email || 'N/A',
        formatNameFromAddress(customer.shippingAddress),
        customer.shippingAddress?.company || 'N/A',
        customer.shippingAddress?.address1 || 'N/A',
        customer.shippingAddress?.address2 || 'N/A',
        customer.shippingAddress?.city || 'N/A',
        customer.shippingAddress?.state || 'N/A',
        customer.shippingAddress?.postal || 'N/A',
        customer.shippingAddress?.country || 'N/A',
        customer.shippingAddress?.phone || 'N/A',
        customer.shippingAddress?.email || 'N/A',
      ],
    ];

    const csvContent = csvData.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `customer-${customer.id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'Success',
      description: 'Customer exported successfully',
    });
  }, [customer, toast, formatTz]);

  return (
    <div className="flex w-full flex-col items-center justify-between">
      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4">
          <BackButton to={returnUrl || orgRoutes.CUSTOMERS()} />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('details.title')}</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              <DateDisplay value={customer.createdAt} />
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="flex items-center gap-2">
            {isDraftCustomer && (
              <Badge variant="yellow" className="gap-1">
                <FlaskConical className="h-3 w-3" />
                Draft
              </Badge>
            )}
            <CopyableResourceBadge name="Customer ID" value={customer.id} variant="outline" />
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!isTzReady}>
            <Download className="mr-2 h-4 w-4" />
            {t('export')}
          </Button>
        </div>
      </div>

      {/* Customer Tags Section */}
      <div className="mt-4 w-full">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-sm font-medium">{t('tags.title')}:</span>
          <CustomerTagsBadge
            tags={(customer as any).tags || []}
            onTagAdd={handleAddTag}
            onTagRemove={handleRemoveTag}
            editable={true}
            maxVisible={5}
          />
        </div>
      </div>
    </div>
  );
}
